<template>
    <v-modal title="Make a Payment" @close="$emit('close')" size="small" scroll>
        <div slot="contents" class="position-relative">
            <v-inline-loader v-if="isLoading"/>
            <div class="modal-content" v-if="$v.$error">
                <div class="grid-item grid-item-full">
                    <v-error-messages>
                        <li class="list-item" v-if="!$v.paymentAmount.required">
                            Payment amount required
                        </li>
                        <li class="list-item" v-if="!$v.paymentAmount.positiveAmountUnlessInvoiceIsZero">
                            Payment amount must be greater than {{0 | monetizeExplicit(invoice.currency)}}
                        </li>
                        <li class="list-item" v-if="!$v.paymentAmount.maxValue">
                            Payment amount must be less than {{amountOwed | monetizeExplicit(invoice.currency)}}
                        </li>
                        <li class="list-item" v-if="!$v.paymentAmount.isFloat && $v.paymentAmount.required">
                            Payment amount must be a number
                        </li>
                    </v-error-messages>
                </div>
            </div>
            <div class="modal-content">
                <div class="grid">
                    <div class="grid-item grid-item-half">
                        <r-input
                            label="Amount"
                            left-label="$"
                            v-model="paymentAmount"/>
                    </div>
                </div>
            </div>
            <div class="modal-content">
                <v-radio
                    class="stack-m"
                    label="Process with Payment Instrument"
                    :value="false"
                    @update="updateUsePaymentInstrument"
                    :model="payOutside"
                    group="usePaymentInstrumentGroup"/>
                <v-radio
                    label="Paid outside Rebilly"
                    :value="true"
                    @update="updateUsePaymentInstrument"
                    :model="payOutside"
                    group="usePaymentInstrumentGroup"/>
            </div>
            <invoice-payment-card
                v-if="!payOutside"
                :is-switching="isSwitching"
                :payment-instrument="paymentInstrument"
                :payment-instruments="paymentInstruments"
                :show-instruments="showInstruments"
                @change-instrument="changeInstrument"
                @show-instruments="showInstruments = !showInstruments"
            />
            <template v-else>
                <div class="modal-content modal-content-secondary">
                    <v-select :preselect-first="true" v-model="selectedPaidOutsideMethod" label="Payment Method" :options="paidOutsideMethods"></v-select>
                </div>
                <template v-if="payByCash">
                    <div class="modal-content modal-content-secondary">
                        <r-input label="Received By" v-model="receivedBy"/>
                    </div>
                    <div class="modal-content modal-content-secondary">
                        <v-date-input label="Processed Time" type="datetime" v-model="processedTime"/>
                    </div>
                </template>
                <template v-else-if="payByCheck">
                    <div class="modal-content modal-content-secondary">
                        <r-input label="Reference" v-model="reference"/>
                    </div>
                    <div class="modal-content modal-content-secondary">
                        <v-date-input label="Processed Time" type="datetime" v-model="processedTime"/>
                    </div>
                </template>
                <invoice-payment-card
                    v-else
                    :is-switching="isSwitching"
                    :payment-instrument="paymentInstrument"
                    :payment-instruments="paymentInstruments"
                    :show-instruments="showInstruments"
                    @change-instrument="changeInstrument"
                    @show-instruments="showInstruments = !showInstruments"
                />
            </template>
        </div>
        <template slot="actions">
            <r-button
                :disabled="isLoading || !canProcess"
                type="primary"
                @click="submit"
            >Submit</r-button>
        </template>
    </v-modal>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import vInlineLoader from '@/components/v-inline-loader';
    import vPaymentInstrument from '@/components/payment-instrument/v-payment-instrument';
    import {
        vErrorMessages, vCheckbox, vRadio, vSelect, vDateInput,
    } from '@/components/form';
    import invoicePaymentCard from '@/modules/invoices/components/invoice-payment-card';
    import {required, maxValue, or} from 'vuelidate/lib/validators';
    import greaterThan from '@/common/validation/validators/greater-than';
    import PaymentInstrumentModel from '@/common/models/payment-instrument-model';
    import PaymentInstrumentExpandedModel from '@/common/models/payment-instrument-expanded-model';

    export default {
        components: {
            invoicePaymentCard,
            vModal,
            vErrorMessages,
            vCheckbox,
            vPaymentInstrument,
            vInlineLoader,
            vRadio,
            vSelect,
            vDateInput,
        },
        props: {
            customer: {type: Object},
            invoice: {type: Object},
            amountOwed: {type: Number},
        },
        data() {
            return {
                isLoading: false,
                isSwitching: false,
                showInstruments: false,
                paymentAmount: this.amountOwed || this.invoice.amount,
                payOutside: false,
                paymentInstrument: this.customer.defaultPaymentInstrument,
                paymentInstruments: [],
                receivedBy: null,
                reference: null,
                processedTime: this.getImmediateTime(),
                selectedPaidOutsideMethod: null,
                paidOutsideMethods: [
                    {label: 'Paid Outside with Existing Payment Instruments', value: PaymentInstrumentModel.Methods.paymentCard},
                    {label: 'Cash', value: PaymentInstrumentModel.Methods.cash},
                    {label: 'Check', value: PaymentInstrumentModel.Methods.check},
                ],
            };
        },
        validations() {
            return {
                paymentAmount: {
                    required,
                    isFloat: value => !isNaN(parseFloat(value)),
                    positiveAmountUnlessInvoiceIsZero: or(v => Number(v) === this.amountOwed, greaterThan(0)),
                    maxValue: maxValue(this.amountOwed),
                },
            };
        },
        computed: {
            payByCash() {
                return this.selectedPaidOutsideMethod === PaymentInstrumentModel.Methods.cash;
            },
            payByCheck() {
                return this.selectedPaidOutsideMethod === PaymentInstrumentModel.Methods.check;
            },
            canProcess() {
                if (this.payOutside === false) {
                    return this.paymentInstrument ? !this.paymentInstrument.isEmpty : false;
                }
                if (this.payOutside === true && this.paymentInstrument.isEmpty) {
                    return this.payByCheck || this.payByCash;
                }
                return true;
            },
        },
        methods: {
            submit() {
                this.$v.$touch();
                if (!this.$v.$invalid) {
                    let paymentInstrument = {};
                    let processedTime = '';
                    if (this.processedTime) {
                        processedTime = this.processedTime.format();
                    }
                    if (this.payByCash) {
                        paymentInstrument = new PaymentInstrumentExpandedModel({
                            method: PaymentInstrumentModel.Methods.cash,
                            receivedBy: this.receivedBy,
                        });
                    } else if (this.payByCheck) {
                        paymentInstrument = new PaymentInstrumentExpandedModel({
                            method: PaymentInstrumentModel.Methods.check,
                            reference: this.reference,
                        });
                    } else {
                        /* eslint-disable prefer-destructuring */
                        paymentInstrument = this.paymentInstrument;
                        processedTime = null;
                    }
                    this.$emit('pay-invoice', this.paymentAmount, paymentInstrument, processedTime, this.payOutside);
                    this.$emit('close');
                }
            },
            async fetchData() {
                this.isLoading = true;
                try {
                    const params = {filter: `customerId:${this.customer.id};status:active`};
                    const compoundResponse = await Promise.all(
                        this.$api(
                            ({bankAccounts, paymentCards, paypalAccounts}) => [
                                bankAccounts.getAll(params),
                                paymentCards.getAll(params),
                                paypalAccounts.getAll(params),
                            ],
                        ),
                    );
                    this.paymentInstruments = [...this.processPaymentInstruments(compoundResponse)];
                } catch (error) {
                    this.$toast.fromError(error);
                    this.$emit('close');
                } finally {
                    this.isLoading = false;
                }
            },
            processPaymentInstruments([bankAccounts, paymentCards, paypalAccounts]) {
                const instrumentTypeTuple = [
                    [bankAccounts],
                    [paymentCards],
                    [paypalAccounts],
                ];
                const reduceCallback = (allInstruments, [{items}]) => {
                    const paymentInstruments = items
                        .map(({fields}) => new PaymentInstrumentExpandedModel(fields));
                    return allInstruments.concat(paymentInstruments);
                };
                return instrumentTypeTuple.reduce(reduceCallback, []);
            },
            changeInstrument(instrument) {
                this.isSwitching = true;
                // fake load time to ease UX
                setTimeout(() => {
                    this.paymentInstrument = instrument;
                    this.isSwitching = false;
                }, 500);
                this.showInstruments = false;
            },
            updateUsePaymentInstrument(value) {
                this.payOutside = value;
            },
            getImmediateTime() {
                return this.$tz().now();
            },
        },
        created() {
            this.fetchData();
        },
    };
</script>
